import { VARIANCE_ACTIONS, varianceActionTargetFile } from './varianceActions.js';

const normalizeCode = (value) => String(value || '').trim().toUpperCase();
const normalizeId = (value) => String(value || '').trim().toLowerCase();
const normalizeIdentityKey = (value) => String(value || '').trim().toLowerCase();

const sameProduct = (action, code) => {
  const actionCode = normalizeCode(action.productCode || action.customProductCode);
  return Boolean(actionCode && actionCode === normalizeCode(code));
};

const isInheritedPending = (action = {}) => (
  (action.saveStatus || action.status) === 'inherited_pending'
);

const rowKeyCandidates = (row = {}) => {
  const source = row || {};
  return [
    source.sourceRowKey,
    source.rowKey,
    source.storageKey,
    source.fileRowId,
    source.id,
    source.rowNumber,
  ].filter((value) => value !== undefined && value !== null && String(value).trim() !== '').map((value) => String(value).trim().toLowerCase());
};

const actionRowKeyCandidates = (action = {}) => [
  action.sourceRowKey,
  action.targetRowKey,
  action.sourceEvidence?.rowKey,
  action.sourceEvidence?.id,
  action.targetEvidence?.rowKey,
  action.targetEvidence?.id,
].filter((value) => value !== undefined && value !== null && String(value).trim() !== '').map((value) => String(value).trim().toLowerCase());

const sameRow = (action, row) => {
  const rowKeys = rowKeyCandidates(row);
  if (!rowKeys.length) return false;
  return actionRowKeyCandidates(action).some((key) => rowKeys.includes(key));
};

const structuredIdentityCandidates = (source = {}) => {
  const orderIds = [
    source.orderItemId,
    source.__orderItemId,
    source.manualOrderItemId,
    source.accountMatch?.orderItemId,
    source.accountMatch?.evidence?.orderItemId,
  ].map(normalizeId).filter(Boolean);
  const referenceIds = [
    source.referenceId,
    source.referenceID,
    source.ReferenceId,
    source.manualReferenceId,
    source.accountMatch?.referenceId,
    source.accountMatch?.evidence?.referenceId,
  ].map(normalizeId).filter(Boolean);
  return [
    source.productIdentityKey,
    source.metadata?.productIdentityKey,
    ...orderIds.map((id) => `order-item:${id}`),
    ...referenceIds.map((id) => `reference:${id}`),
    ...rowKeyCandidates(source).map((key) => `row:${key}`),
  ].map(normalizeIdentityKey).filter(Boolean);
};

const productEvidenceIdentities = (source = {}) => {
  const code = normalizeCode(source.productCode || source.oemProductCode || source.normalizedProductCode);
  if (!code) return [];
  return [
    source.orderItemId,
    source.__orderItemId,
    source.manualOrderItemId,
    source.referenceId,
    source.referenceID,
    source.manualReferenceId,
    source.accountMatch?.orderItemId,
    source.accountMatch?.referenceId,
  ].map(normalizeId)
    .filter(Boolean)
    .map((id) => `${id}|${code}`);
};

const actionEvidenceIdentities = (action = {}) => {
  const actionCode = normalizeCode(action.productCode || action.sourceProductCode || action.replacementProductCode || action.customProductCode);
  const directIds = [
    action.manualOrderItemId,
    action.manualReferenceId,
    action.orderItemId,
    action.referenceId,
  ].map(normalizeId).filter(Boolean);
  const direct = actionCode ? directIds.map((id) => `${id}|${actionCode}`) : [];
  return [
    ...direct,
    ...productEvidenceIdentities(action.sourceEvidence || {}),
    ...productEvidenceIdentities(action.sourceEvidence?.row || {}),
    ...productEvidenceIdentities(action.targetEvidence || {}),
    ...productEvidenceIdentities(action.targetEvidence?.row || {}),
  ];
};

const sameEvidenceIdentity = (action, row, productCode) => {
  const rowIdentities = new Set([
    ...productEvidenceIdentities({ ...(row || {}), productCode }),
    ...productEvidenceIdentities(row || {}),
  ]);
  if (!rowIdentities.size) return false;
  return actionEvidenceIdentities(action).some((identity) => rowIdentities.has(identity));
};

const sameStructuredIdentity = (action, row) => {
  const actionIdentities = new Set([
    action.productIdentityKey,
    action.metadata?.productIdentityKey,
    action.identity?.key,
    action.sourceEvidence?.productIdentityKey,
    action.sourceEvidence?.metadata?.productIdentityKey,
    action.sourceEvidence?.row?.productIdentityKey,
    action.targetEvidence?.productIdentityKey,
    action.targetEvidence?.metadata?.productIdentityKey,
    action.targetEvidence?.row?.productIdentityKey,
  ].map(normalizeIdentityKey).filter(Boolean));
  if (!actionIdentities.size) return false;
  return structuredIdentityCandidates(row || {}).some((identity) => actionIdentities.has(identity));
};

const statusPrefix = (action) => {
  if (action.saveStatus === 'resolved') return 'Resolved';
  if (action.saveStatus === 'exported') return 'Exported';
  if (action.saveStatus === 'saved') return 'Saved';
  if ((action.saveStatus || action.status) === 'inherited_pending') return 'Inherited pending';
  return 'Draft';
};

const previewLabel = (action) => {
  const prefix = statusPrefix(action);
  if (action.actionType === VARIANCE_ACTIONS.REMOVE_FROM_GM || action.actionType === VARIANCE_ACTIONS.REMOVE_FROM_CPQ) return `${prefix} remove`;
  if (action.actionType === VARIANCE_ACTIONS.REPLACE_PRODUCT) return `${prefix} replace`;
  if (action.actionType === VARIANCE_ACTIONS.CHANGE_PRICE) return `${prefix} price`;
  if (action.actionType === VARIANCE_ACTIONS.FORCE_EVIDENCE_MATCH) return `${prefix} evidence`;
  if (action.actionType === VARIANCE_ACTIONS.CUSTOM) return `${prefix} custom`;
  return `${prefix} add`;
};

const hasEvidenceBinding = (action = {}) => Boolean(
  action.manualOrderItemId
  || action.manualReferenceId
  || action.bindingMethod
  || action.salesforceAccountId
);

export const draftPreviewForProduct = ({ actions = [], fileKind, productCode, row = null, allowWeakProductCodeMatch = false }) => {
  const matching = actions.filter((action) => {
    const actionHasRowKey = actionRowKeyCandidates(action).length > 0;
    const rowHasKey = rowKeyCandidates(row).length > 0;
    if (actionHasRowKey && rowHasKey && sameRow(action, row)) {
      // Exact row keys remain the strongest same-file match.
    } else if (sameStructuredIdentity(action, { ...(row || {}), productCode })) {
      // Backend inherited product identity can outlive row/product-code formatting changes.
    } else if (sameEvidenceIdentity(action, row, productCode)) {
      // Composite order/reference id + product code is specific enough for duplicate refs.
    } else if (actionHasRowKey && rowHasKey) {
      if (!(allowWeakProductCodeMatch && isInheritedPending(action) && sameProduct(action, productCode))) return false;
    } else if (!sameProduct(action, productCode)) {
      return false;
    }
    const explicitTarget = action.targetSource || action.targetFile;
    if (explicitTarget === 'gm' || explicitTarget === 'cpq') return explicitTarget === fileKind;
    const targetFile = varianceActionTargetFile(action.actionType);
    if (targetFile) return targetFile === fileKind;
    if (action.actionType === VARIANCE_ACTIONS.FORCE_EVIDENCE_MATCH) {
      return (action.targetSource || action.targetFile) === fileKind;
    }
    return action.source === fileKind;
  });
  if (!matching.length) return null;

  const evidenceAction = matching.find((action) => action.actionType === VARIANCE_ACTIONS.FORCE_EVIDENCE_MATCH);
  const inheritedCode = evidenceAction?.sourceProductCode && normalizeCode(evidenceAction.sourceProductCode) !== normalizeCode(productCode)
    ? evidenceAction.sourceProductCode
    : null;
  const withEvidence = (preview) => {
    const embeddedEvidence = hasEvidenceBinding(preview.action) ? preview.action : null;
    const effectiveEvidenceAction = evidenceAction || embeddedEvidence;
    if (!effectiveEvidenceAction) return preview;
    return {
      ...preview,
      evidenceAction: effectiveEvidenceAction,
      replacementProductCode: preview.replacementProductCode || inheritedCode || effectiveEvidenceAction.replacementProductCode || effectiveEvidenceAction.sourceProductCode,
      label: preview.action?.id && preview.action.id !== effectiveEvidenceAction.id
        ? `${preview.label} + evidence`
        : embeddedEvidence
        ? `${preview.label} + evidence`
        : preview.label,
    };
  };

  const removeAction = matching.find((action) => (
    action.actionType === VARIANCE_ACTIONS.REMOVE_FROM_GM
    || action.actionType === VARIANCE_ACTIONS.REMOVE_FROM_CPQ
  ));
  if (removeAction) return withEvidence({ variant: 'removed', label: previewLabel(removeAction), action: removeAction });

  const replaceAction = matching.find((action) => action.actionType === VARIANCE_ACTIONS.REPLACE_PRODUCT);
  if (replaceAction) {
    return withEvidence({
      variant: 'replaced',
      label: previewLabel(replaceAction),
      replacementProductCode: replaceAction.replacementProductCode,
      proposedPrice: replaceAction.proposedPrice,
      action: replaceAction,
    });
  }

  const priceAction = matching.find((action) => action.actionType === VARIANCE_ACTIONS.CHANGE_PRICE);
  if (priceAction) {
    return withEvidence({
      variant: 'price',
      label: previewLabel(priceAction),
      proposedPrice: priceAction.proposedPrice,
      action: priceAction,
    });
  }

  const customAction = matching.find((action) => action.actionType === VARIANCE_ACTIONS.CUSTOM);
  if (customAction) {
    const mutation = customAction.compiledMutation || 'adjust';
    return withEvidence({
      variant: mutation === 'remove' ? 'removed' : mutation === 'adjust' ? 'price' : 'custom',
      label: previewLabel(customAction),
      replacementProductCode: customAction.customProductCode,
      proposedPrice: customAction.customPrice,
      action: customAction,
    });
  }

  if (evidenceAction) {
    return {
      variant: 'evidence',
      label: previewLabel(evidenceAction),
      replacementProductCode: inheritedCode,
      action: evidenceAction,
    };
  }

  return null;
};

export const draftAddsForFile = ({ actions = [], fileKind }) => actions
  .filter((action) => (action.targetSource || action.targetFile || varianceActionTargetFile(action.actionType)) === fileKind)
  .filter((action) => (
    action.actionType === VARIANCE_ACTIONS.ADD_TO_GM
    || action.actionType === VARIANCE_ACTIONS.ADD_TO_CPQ
    || (action.actionType === VARIANCE_ACTIONS.CUSTOM && action.compiledMutation === 'add')
  ))
  .map((action) => ({
    id: action.id,
    productName: action.productName || action.productCode || 'Draft product',
    productCode: action.customProductCode || action.productCode,
    price: action.customPrice ?? action.proposedPrice ?? action.price,
    status: action.productStatus || 'LIVE',
    label: previewLabel(action),
    action,
  }));
